import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  message,
  Card,
  Typography,
  Collapse,
  Alert,
} from 'antd';
import { SaveOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type { KrybotAgent, KrybotAgentCreate, KrybotAgentUpdate, AgentType, KrybotTemplate } from '../../../types/krybot';
import { AgentTypeSelectOptions } from '../AgentTypeSelectOptions';
import { JsonSchemaEditor } from './JsonSchemaEditor';
import styles from './AgentManagement.module.css';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

interface AgentFormProps {
  agent?: KrybotAgent | null;
  onSave: () => void;
  onCancel: () => void;
}

export const AgentForm: React.FC<AgentFormProps> = ({ agent, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [availableAgents, setAvailableAgents] = useState<KrybotAgent[]>([]);
  const [templates, setTemplates] = useState<KrybotTemplate[]>([]);
  const [fullAgent, setFullAgent] = useState<KrybotAgent | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<KrybotTemplate | null>(null);
  const [templateAutoPopulated, setTemplateAutoPopulated] = useState(false);

  const loadAvailableAgents = useCallback(async () => {
    try {
      const agents = await KrybotService.listAgents();
      setAvailableAgents(agents);
    } catch (err) {
      // Silently fail - parent agents are optional
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const templateList = await KrybotService.listTemplates();
      // Include active templates, plus the agent's current template (if any) even if inactive
      const activeTemplates = templateList.filter(t => t.is_active);
      const agentToUse = fullAgent || agent;
      if (agentToUse?.template_id) {
        const currentTemplate = templateList.find(t => t.id === agentToUse.template_id);
        if (currentTemplate) {
          // Add the agent's current template even if it's inactive
          if (!activeTemplates.find(t => t.id === currentTemplate.id)) {
            activeTemplates.push(currentTemplate);
          }
        } else {
          console.warn(`Template ${agentToUse.template_id} not found in template list`);
        }
      }
      setTemplates(activeTemplates);
    } catch (err) {
      console.error('Failed to load templates:', err);
      // Silently fail - templates are optional
    }
  }, [fullAgent, agent]);

  useEffect(() => {
    loadAvailableAgents();
  }, [loadAvailableAgents]);

  // Fetch full agent details when editing
  useEffect(() => {
    const fetchFullAgent = async () => {
      if (agent?.id) {
        try {
          const full = await KrybotService.getAgent(agent.id);
          setFullAgent(full);
        } catch (err) {
          console.error('Failed to fetch full agent details:', err);
          setFullAgent(agent); // Fallback to provided agent
        }
      } else {
        setFullAgent(null);
      }
    };
    fetchFullAgent();
  }, [agent?.id, agent]);

  // Reload templates when agent/fullAgent changes to include the agent's current template
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Populate form when agent data is available (don't wait for templates)
  useEffect(() => {
    const agentToUse = fullAgent || agent;
    
    if (agentToUse) {
      // Convert JSON fields to objects (JsonSchemaEditor expects objects)
      // Use structuredClone or JSON parse/stringify to avoid circular references
      const safeClone = <T,>(obj: T | undefined): T | {} => {
        if (!obj) return {};
        try {
          return JSON.parse(JSON.stringify(obj)) as T;
        } catch {
          return {};
        }
      };

      const formValues = {
        name: agentToUse.name || '',
        display_name: agentToUse.display_name || '',
        description: agentToUse.description || '',
        agent_type: agentToUse.agent_type || '',
        persona_prompt: agentToUse.persona_prompt || '',
        capabilities: safeClone(agentToUse.capabilities),
        default_context: safeClone(agentToUse.default_context),
        input_schema: safeClone(agentToUse.input_schema),
        output_schema: safeClone(agentToUse.output_schema),
        is_active: agentToUse.is_active ?? true,
        parent_agent_id: agentToUse.parent_agent_id || undefined,
        template_id: agentToUse.template_id || undefined,
      };
      // Use resetFields to ensure all fields are updated, including clearing previous values
      form.setFieldsValue(formValues);
    } else {
      // Reset form when no agent (creating new)
      form.resetFields();
    }
  }, [agent, fullAgent, form]);

  // Update template_id once templates are loaded (if agent has a template_id)
  useEffect(() => {
    const agentToUse = fullAgent || agent;
    if (agentToUse && templates.length > 0) {
      const templateId = agentToUse.template_id;
      // Verify the template exists in the templates list
      const templateExists = templateId ? templates.some(t => t.id === templateId) : true;
      if (templateId && !templateExists) {
        console.warn(`Agent template_id ${templateId} not found in available templates`);
      }
      // Update template_id field if it exists
      if (templateId !== undefined) {
        form.setFieldValue('template_id', templateId);
        // Set selected template
        const template = templates.find(t => t.id === templateId);
        if (template) {
          setSelectedTemplate(template);
        }
      }
    }
  }, [agent, fullAgent, form, templates]);

  // Handle template selection change and auto-populate schemas
  const handleTemplateChange = async (templateId: string | undefined) => {
    if (!templateId) {
      setSelectedTemplate(null);
      setTemplateAutoPopulated(false);
      return;
    }

    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      
      // Auto-populate input_schema and output_schema from template.metadata
      if (template.metadata) {
        const metadata = template.metadata;
        
        // Check if schemas are already set (don't overwrite if user has already set them)
        const currentInputSchema = form.getFieldValue('input_schema');
        const currentOutputSchema = form.getFieldValue('output_schema');
        
        const hasInputSchema = currentInputSchema && currentInputSchema.trim();
        const hasOutputSchema = currentOutputSchema && currentOutputSchema.trim();
        
        // Extract schemas from metadata
        // Template metadata may contain input_schema and output_schema directly
        // Or the entire metadata might be the schema
        let inputSchema = metadata.input_schema || (metadata.type === 'input' ? metadata : null);
        let outputSchema = metadata.output_schema || (metadata.type === 'output' ? metadata : null);
        
        // If metadata itself looks like a schema, use it for output
        if (!outputSchema && metadata.$schema) {
          outputSchema = metadata;
        }
        
        // Auto-populate if fields are empty
        if (inputSchema && !hasInputSchema) {
          form.setFieldValue('input_schema', JSON.stringify(inputSchema, null, 2));
        }
        if (outputSchema && !hasOutputSchema) {
          form.setFieldValue('output_schema', JSON.stringify(outputSchema, null, 2));
        }
        
        if ((inputSchema && !hasInputSchema) || (outputSchema && !hasOutputSchema)) {
          setTemplateAutoPopulated(true);
          message.info(`Schemas populated from template: ${template.template_key}`, 3);
        }
      }
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      // Parse JSON fields - handle both string and object formats
      const parseJsonField = (field: string | Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
        if (!field) return undefined;
        if (typeof field === 'object') return field;
        if (typeof field === 'string' && field.trim()) {
          try {
            return JSON.parse(field);
          } catch {
            return undefined;
          }
        }
        return undefined;
      };

      const payload: KrybotAgentCreate | KrybotAgentUpdate = {
        name: values.name,
        display_name: values.display_name,
        description: values.description,
        agent_type: values.agent_type,
        persona_prompt: values.persona_prompt,
        capabilities: parseJsonField(values.capabilities),
        default_context: parseJsonField(values.default_context),
        input_schema: parseJsonField(values.input_schema),
        output_schema: parseJsonField(values.output_schema),
        is_active: values.is_active ?? true,
        parent_agent_id: values.parent_agent_id || undefined,
        template_id: values.template_id || undefined,
      };

      if (agent) {
        await KrybotService.updateAgent(agent.id, payload as KrybotAgentUpdate);
        message.success('Agent updated successfully');
      } else {
        await KrybotService.createAgent(payload as KrybotAgentCreate);
        message.success('Agent created successfully');
      }

      onSave();
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save agent';
      message.error(`Failed to save agent: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const validateJson = (_: unknown, value: string | Record<string, unknown>) => {
    if (!value) {
      return Promise.resolve();
    }
    
    // If it's already an object, it's valid
    if (typeof value === 'object') {
      return Promise.resolve();
    }
    
    // If it's a string, try to parse it
    if (typeof value === 'string') {
      if (!value.trim()) {
        return Promise.resolve();
      }
      try {
        JSON.parse(value);
        return Promise.resolve();
      } catch {
        return Promise.reject(new Error('Invalid JSON format'));
      }
    }
    
    return Promise.resolve();
  };

  // Compute initial values from agent data
  const getInitialValues = () => {
    const agentToUse = fullAgent || agent;
    if (agentToUse) {
      return {
        name: agentToUse.name,
        display_name: agentToUse.display_name,
        description: agentToUse.description,
        agent_type: agentToUse.agent_type,
        persona_prompt: agentToUse.persona_prompt,
        capabilities: agentToUse.capabilities || {},
        default_context: agentToUse.default_context || {},
        input_schema: agentToUse.input_schema || {},
        output_schema: agentToUse.output_schema || {},
        is_active: agentToUse.is_active ?? true,
        parent_agent_id: agentToUse.parent_agent_id,
        template_id: agentToUse.template_id,
      };
    }
    return { is_active: true };
  };

  return (
    <div className="agent-form">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={getInitialValues()}
      >
        <Form.Item
          name="name"
          label="Agent Name"
          rules={[{ required: true, message: 'Please enter agent name' }]}
        >
          <Input placeholder="e.g., prime-architect" disabled={!!agent} />
        </Form.Item>

        <Form.Item name="display_name" label="Display Name">
          <Input placeholder="e.g., Prime Architect" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} placeholder="Describe the agent's purpose and responsibilities" />
        </Form.Item>

        <Form.Item
          name="agent_type"
          label="Agent Type"
          rules={[{ required: true, message: 'Please select agent type' }]}
        >
          <Select placeholder="Select agent type" showSearch optionFilterProp="children">
            <AgentTypeSelectOptions />
          </Select>
        </Form.Item>

        <Form.Item name="parent_agent_id" label="Parent Agent (Optional)">
          <Select
            placeholder="Select parent agent for sub-agents"
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {availableAgents
              .filter((a) => a.id !== agent?.id)
              .map((a) => (
                <Option key={a.id} value={a.id}>
                  {a.display_name || a.name} ({a.agent_type})
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item name="template_id" label="Template (Optional)">
          <Select
            placeholder="Select template for structured output"
            allowClear
            showSearch
            optionFilterProp="children"
            onChange={handleTemplateChange}
          >
            {templates.map((t) => (
              <Option key={t.id} value={t.id}>
                {t.template_key} ({t.template_type})
              </Option>
            ))}
          </Select>
        </Form.Item>
        {templateAutoPopulated && (
          <Alert
            message="Schemas Auto-Populated"
            description="Input and/or output schemas have been automatically populated from the selected template. You can modify them if needed."
            type="info"
            icon={<InfoCircleOutlined />}
            closable
            onClose={() => setTemplateAutoPopulated(false)}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.template_id !== currentValues.template_id}>
          {({ getFieldValue }) => {
            const templateId = getFieldValue('template_id');
            if (templateId) {
              const template = templates.find(t => t.id === templateId);
              if (template?.prompt_text) {
                return (
                  <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                    <strong>Template Prompt:</strong> {template.prompt_text}
                  </div>
                );
              }
            }
            return null;
          }}
        </Form.Item>

        <Form.Item name="persona_prompt" label="Persona Prompt">
          <TextArea
            rows={8}
            placeholder="System prompt that defines the agent's persona and behavior"
          />
        </Form.Item>

        <Collapse 
          defaultActiveKey={['capabilities', 'default_context', 'input_schema', 'output_schema']} 
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'capabilities',
              label: 'Capabilities',
              children: (
                <Form.Item
                  name="capabilities"
                  rules={[{ validator: validateJson }]}
                  help="Define what actions this agent can perform. Use the visual editor or switch to code mode."
                >
                  <JsonSchemaEditor
                    fieldType="capabilities"
                    agentType={form.getFieldValue('agent_type')}
                    template={selectedTemplate || undefined}
                    disabled={loading}
                  />
                </Form.Item>
              ),
            },
            {
              key: 'default_context',
              label: 'Default Context',
              children: (
                <Form.Item
                  name="default_context"
                  rules={[{ validator: validateJson }]}
                  help="Default context information that will be provided to the agent for all operations."
                >
                  <JsonSchemaEditor
                    fieldType="default_context"
                    agentType={form.getFieldValue('agent_type')}
                    template={selectedTemplate || undefined}
                    disabled={loading}
                  />
                </Form.Item>
              ),
            },
            {
              key: 'input_schema',
              label: 'Input Schema (JSON Schema)',
              children: (
                <Form.Item
                  name="input_schema"
                  rules={[{ validator: validateJson }]}
                  help="JSON Schema that defines the structure of input data the agent expects. May be auto-populated from template."
                >
                  <JsonSchemaEditor
                    fieldType="input_schema"
                    agentType={form.getFieldValue('agent_type')}
                    template={selectedTemplate || undefined}
                    placeholder='{"type": "object", "properties": {...}}'
                    disabled={loading}
                  />
                </Form.Item>
              ),
            },
            {
              key: 'output_schema',
              label: 'Output Schema (JSON Schema)',
              children: (
                <Form.Item
                  name="output_schema"
                  rules={[{ validator: validateJson }]}
                  help="JSON Schema that defines the structure of output data the agent will return. May be auto-populated from template."
                >
                  <JsonSchemaEditor
                    fieldType="output_schema"
                    agentType={form.getFieldValue('agent_type')}
                    template={selectedTemplate || undefined}
                    placeholder='{"type": "object", "properties": {...}}'
                    disabled={loading}
                  />
                </Form.Item>
              ),
            },
          ]}
        />

        <Form.Item name="is_active" valuePropName="checked" label="Active">
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
              {agent ? 'Update' : 'Create'} Agent
            </Button>
            <Button icon={<CloseOutlined />} onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

